/**
 * Normalized error contract for API-backed patient data.
 *
 * openapi-fetch errors are usually plain objects rather than Error instances.
 * Keeping their normalization here prevents query hooks from either producing
 * "[object Object]" or, worse, converting a failed request into an empty list.
 */

type ApiErrorRecord = Record<string, unknown>;

function asRecord(value: unknown): ApiErrorRecord | null {
  return value && typeof value === "object" ? (value as ApiErrorRecord) : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function findMessage(error: unknown, seen: Set<unknown>): string | null {
  if (error instanceof Error) return nonEmptyString(error.message);
  const direct = nonEmptyString(error);
  if (direct) return direct;

  const record = asRecord(error);
  if (!record || seen.has(error)) return null;
  seen.add(error);

  for (const key of ["message", "detail", "title", "error_description"]) {
    const message = nonEmptyString(record[key]);
    if (message) return message;
  }

  for (const key of ["error", "cause", "response", "body"]) {
    const nested = findMessage(record[key], seen);
    if (nested) return nested;
  }

  if (Array.isArray(record.errors)) {
    for (const item of record.errors) {
      const nested = findMessage(item, seen);
      if (nested) return nested;
    }
  }

  return null;
}

function findStatus(error: unknown, seen: Set<unknown>): number | undefined {
  const record = asRecord(error);
  if (!record || seen.has(error)) return undefined;
  seen.add(error);

  for (const key of ["status", "statusCode"]) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && /^\d{3}$/.test(value)) {
      return Number(value);
    }
  }

  for (const key of ["error", "cause", "response"]) {
    const nested = findStatus(record[key], seen);
    if (nested !== undefined) return nested;
  }

  return undefined;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "The request could not be completed",
): string {
  return findMessage(error, new Set()) ?? fallback;
}

/** Reads the HTTP status from an openapi-fetch result without inventing types. */
export function getApiResponseStatus(result: unknown): number | undefined {
  return findStatus(result, new Set());
}

export class ApiRequestError extends Error {
  readonly status?: number;
  readonly context?: string;

  constructor(
    message: string,
    options: {
      cause?: unknown;
      status?: number;
      context?: string;
    } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = "ApiRequestError";
    this.status = options.status;
    this.context = options.context;
  }
}

export function toApiError(
  error: unknown,
  fallback: string,
  context?: string,
  status?: number,
): ApiRequestError {
  if (error instanceof ApiRequestError) return error;
  // An explicit HTTP status (from the `response` in scope) wins, because the
  // openapi-fetch error body often does not carry the status itself.
  const resolvedStatus = status ?? findStatus(error, new Set());
  return new ApiRequestError(getApiErrorMessage(error, fallback), {
    cause: error,
    status: resolvedStatus,
    context,
  });
}

export function throwApiError(
  error: unknown,
  fallback: string,
  context?: string,
  status?: number,
): never {
  throw toApiError(error, fallback, context, status);
}

export function isAbortError(error: unknown): boolean {
  const record = asRecord(error);
  return record?.name === "AbortError";
}

/**
 * Converts a rejected fetch into the same error contract as HTTP failures.
 * Caller-driven aborts remain cancellation signals and must not be presented
 * to the UI or retry policy as API failures.
 */
export function throwApiTransportError(
  error: unknown,
  fallback: string,
  context?: string,
): never {
  if (isAbortError(error)) throw error;
  throw toApiError(error, fallback, context);
}

/** Runs fetch while normalizing transport failures and preserving aborts. */
export async function fetchApiResponse(
  input: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
  fallback: string,
  context?: string,
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    throwApiTransportError(error, fallback, context);
  }
}
